import type { JSX, ComponentPropsWithoutRef } from 'react';
import { useState, useMemo, useEffect } from 'react';
import { isNil, isNotNil } from '@wistia/type-guards';
import { styled } from 'styled-components';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { formatNameForDisplay } from './formatNameForDisplay';
import { Image } from '../Image';
import type { BrandColorSchemeTypes } from '../ColorSchemeWrapper/ColorSchemeWrapper';
import { brandColorSchemeOptions } from '../ColorSchemeWrapper/ColorSchemeWrapper';

type AvatarInstanceType = 'image' | 'initials';

type AvatarImageLoadStateType = 'error' | 'loaded' | 'loading';

type AvatarSizeType = 'lg' | 'md' | 'sm';

export type AvatarStatus = { type: AvatarInstanceType; state: AvatarImageLoadStateType };

export type AvatarProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * The height and width of the avatar (overrides size prop)
   */
  heightAndWidth?: number;
  /**
   * The URL of the image to display
   */
  imageUrl?: string | null;
  /**
   * A string that will deterministically set the initials & background color of the avatar
   *
   * _note: only first initial shown regardless of characters provided_
   */
  name?: string | null;
  /**
   * A predefined size for the avatar
   */
  size?: AvatarSizeType;
  /**
   * A callback that is invoked after the image has loaded or failed to load
   */
  onImageLoad?: (avatarStatus: AvatarStatus) => void;
};

export const avatarSizeMap = {
  sm: 16,
  md: 24,
  lg: 32,
};

const Initial = ({ initial }: { initial: string }) => (
  <svg
    data-wui-avatar-initial={true}
    style={{ width: '100%', height: '100%', color: 'var(--wui-color-text-on-fill)' }}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 13.5h24"
      id="P"
    />
    <text>
      <textPath
        dominantBaseline="middle"
        fill="currentColor"
        fontSize="16px"
        href="#P"
        startOffset="50%"
        textAnchor="middle"
      >
        {initial}
      </textPath>
    </text>
  </svg>
);

const AvatarWrapper = styled.div<{ $heightAndWidth: number; $colorScheme: ColorSchemeTypes }>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  width: ${({ $heightAndWidth }) => $heightAndWidth}px;
  height: ${({ $heightAndWidth }) => $heightAndWidth}px;
  place-content: center;
  overflow: hidden;
  background-color: var(--wui-color-bg-fill);
  border-radius: var(--wui-border-radius-rounded);
  display: grid;
  user-select: none;

  > * {
    grid-area: 1 / 1;
  }

  > img {
    aspect-ratio: 1 / 1;
  }

  --wui-avatar-inset-opacity: 10%;

  outline: 2px solid rgb(0 0 0 / var(--wui-avatar-inset-opacity));
  outline-offset: -2px;

  [data-wui-avatar-initial] {
    font-family: var(--wui-typography-family-default);
    font-weight: var(--wui-typography-weight-label-bold);
    padding: min(12.5%, 4px);
  }

  &:only-child {
    a:hover & {
      --wui-avatar-inset-opacity: 20%;

      background-color: var(--wui-color-bg-fill-hover);
    }

    a:active & {
      --wui-avatar-inset-opacity: 30%;

      background-color: var(--wui-color-bg-fill-active);
    }
  }
`;

const chooseColorScheme = (name: string | null | undefined): BrandColorSchemeTypes => {
  // default color scheme
  if (isNil(name) || name === 'Anonymous') {
    return 'blue';
  }

  const multiplier = 31;

  const hashCode = (str: string) =>
    Array.from(str).reduce((sum, char) => (Math.imul(multiplier, sum) + char.charCodeAt(0)) | 0, 0); // eslint-disable-line no-bitwise

  const index = Math.abs(hashCode(name)) % brandColorSchemeOptions.length;

  return brandColorSchemeOptions[index] ?? 'blue';
};

/**
 * An avatar uses a photo or initials to represent an identity.
 */
export const Avatar = ({
  imageUrl,
  heightAndWidth,
  name,
  size = 'md',
  onImageLoad,
  ...props
}: AvatarProps): JSX.Element => {
  const [imageLoadState, setImageLoadState] = useState<AvatarImageLoadStateType>('loading');

  // reset image load state when imageUrl changes so that it doesn't get stuck
  // in an error loading state
  useEffect(() => {
    setImageLoadState('loading');
  }, [imageUrl]);

  const handleImageLoad = () => {
    setImageLoadState('loaded');
    onImageLoad?.({ state: 'loaded', type: 'image' });
  };

  const handleImageLoadError = () => {
    setImageLoadState('error');
    onImageLoad?.({ state: 'error', type: 'initials' });
  };

  const avatarSize = heightAndWidth ?? avatarSizeMap[size];
  const avatarColor = useMemo(() => chooseColorScheme(name), [name]);

  return (
    <AvatarWrapper
      $colorScheme={avatarColor}
      $heightAndWidth={avatarSize}
      {...props}
    >
      <Initial initial={formatNameForDisplay(name)} />
      {isNotNil(imageUrl) && imageLoadState !== 'error' && (
        <Image
          alt={isNotNil(name) ? name : ''}
          data-state={imageLoadState}
          fit="cover"
          onError={handleImageLoadError}
          onLoad={handleImageLoad}
          src={imageUrl}
        />
      )}
    </AvatarWrapper>
  );
};

Avatar.displayName = 'Avatar';
